import { useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { CoreBox } from '@youscience/core';

import { RecommenderDocument, RolesDocument, SubjectAreaMapping } from '@youscience/commonapp-service-common';

import AddIcon from '@images/plus.svg?react';

import { ActionButton } from '@components/ActionButton/ActionButton';

import { StyledTeacherButtonGroup } from './UpdateTeacher.styles';

type UpdateTeacherProps = {
  teacherRoles: RolesDocument[];
  recommenders: RecommenderDocument[];
  editTeacher: RecommenderDocument;
  onEditTeacher: (recommenderId: number, subject: string) => Promise<void>;
  onCancel: () => void;
};

export const UpdateTeacher = ({
  teacherRoles,
  recommenders,
  editTeacher,
  onEditTeacher,
  onCancel,
}: UpdateTeacherProps) => {
  const [recommender, setRecommender] = useState<{ recommenderId: string; subject: string }>({
    recommenderId: '',
    subject: '',
  });
  const [teacher, setTeacher] = useState<{ userId: string; subject: string }>({ userId: '', subject: '' });

  const [dropdownValue, setDropdownValue] = useState<string>('');

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [teacherOptions, setTeacherOptions] = useState<{ key: string; value: string; label: string }[]>([]);

  useEffect(() => {
    setTeacher({ userId: '', subject: '' });
    setRecommender({ recommenderId: '', subject: '' });
    setDropdownValue('');

    setTeacherOptions([
      {
        key: `${editTeacher.recommenderId}`,
        value: `recommender:${editTeacher.recommenderId}`,
        label: `${editTeacher.firstName} ${editTeacher.lastName}`,
      },
    ]);
    setDropdownValue(`recommender:${editTeacher.recommenderId}`);
    setRecommender({ recommenderId: editTeacher.recommenderId.toString(), subject: editTeacher.subject as string });
    setTeacher({ userId: '', subject: editTeacher?.subject ?? '' });
  }, [recommenders, teacherRoles, editTeacher]);

  const handleUpdateTeacher = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;

    const recommenderId = +value.split(':')[1];
    const recommender = recommenders.find((recommender) => recommender.recommenderId === recommenderId);

    setRecommender({ recommenderId: recommenderId.toString(), subject: recommender?.subject ?? '' });
    setTeacher({ userId: '', subject: recommender?.subject ?? '' });

    setDropdownValue(value);
  };

  const handleUpdateSubject = (event: SelectChangeEvent<unknown>) => {
    setTeacher((prev) => {
      return { ...prev, subject: event.target.value as string };
    });
  };

  const handleEditTeacher = async () => {
    if (!onEditTeacher) return;

    if (recommender.recommenderId && recommender.subject) {
      setIsSaving(true);
      await onEditTeacher(+recommender.recommenderId, teacher.subject).finally(() => setIsSaving(false));
    }
  };

  const renderValue = (selectedValue: string) => {
    const selectedOption = teacherOptions.find((option) => option.value === selectedValue);

    return selectedOption?.label.split(' - ')[0].trim() ?? '';
  };

  return (
    <CoreBox style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'auto auto' }}>
      <FormControl size="small" sx={{ width: '400px' }}>
        <InputLabel>Teacher</InputLabel>

        <Select
          data-testid="teacher"
          label="Teacher"
          value={dropdownValue}
          onChange={handleUpdateTeacher}
          renderValue={renderValue}
        >
          {teacherOptions.map((option) => (
            <MenuItem key={option.key} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ width: '200px' }}>
        <InputLabel>Subject</InputLabel>
        <Select data-testid="subject" label="Subject" value={teacher?.subject ?? ''} onChange={handleUpdateSubject}>
          {Object.values(SubjectAreaMapping)
            .sort((a, b) => {
              if (a === 'Other') return 1;
              if (b === 'Other') return -1;
              return a.localeCompare(b);
            })
            .map((subject) => (
              <MenuItem key={`subject${subject}`} value={subject}>
                {subject}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <StyledTeacherButtonGroup sx={{ gridColumn: '1 / span 2', justifyContent: 'end' }}>
        <ActionButton onClick={onCancel} variant="outlined" disabled={isSaving}>
          Cancel
        </ActionButton>

        <ActionButton
          onClick={handleEditTeacher}
          busyText="Saving"
          startIcon={<AddIcon />}
          disabled={editTeacher.subject === teacher.subject}
        >
          Save
        </ActionButton>
      </StyledTeacherButtonGroup>
    </CoreBox>
  );
};
